/**
 * Drive agent self-onboarding into the live session (ENG-6527, folded into
 * ENG-6533).
 *
 * ENG-6490 shipped the onboarding state machine + MCP tools + reset/resume
 * endpoints, but nothing ever told a running agent to act on its
 * `onboarding_state`. Setting the state (first-activation seed, `/onboard`,
 * `/resume-onboarding`) was inert. This module is the manager-side driver: when
 * an agent's onboarding is mid-flow (on one of the per-area steps), inject a
 * one-shot directive into its persistent session telling it to run
 * `onboarding_get` and proceed.
 *
 * ADR-0027 / ENG-6561 (R4): onboarding is a per-area walk (framing → tasks →
 * integrations → reporting). Each area is a drivable step, and the nudge is
 * tailored to the *single* current area — it names that area's question and its
 * configure verb (see AREA_DIRECTIVE), never a generic "go configure". The
 * marker re-fires as the agent advances from one area to the next.
 *
 * The host derives the current area straight from `onboarding_state.step`. That
 * step is ALREADY applicable-aware: the API seam folded the live applicable set
 * (role default tasks? integration recommendations?) into the state when it ran
 * START/ADVANCE through `reduceOnboarding(..., {applicable})`, auto-skipping
 * empty areas. The host has no role/catalog data to re-derive that set, so it
 * trusts the persisted step rather than recomputing — the state IS the
 * applicable-set decision, already made. The driver only has to nudge whatever
 * area the agent is parked on.
 *
 * On RESUME the agent re-engages the same area without rewinding (the reducer is
 * idempotent), so the directive both names the areas already behind it ("don't
 * re-ask about those") and points it at `onboarding_get` to re-orient on the
 * current area — re-orient, not re-interview.
 *
 * Idempotency is a per-agent marker keyed on the step + the time it was
 * injected. We inject when the area CHANGES, or when the same area has been
 * outstanding longer than the re-inject interval — the latter self-heals if a
 * session restart wiped the injected directive or the agent ignored it, without
 * spamming every poll. `pending`/`ready` clear the marker so a later `/onboard`
 * (which resets to the first area) re-drives.
 *
 * ENG-6626: the re-nudge is BOUNDED. A busy agent that reads `onboarding_get`
 * but gets pulled back into scheduled-task work can silently drop the ask, and
 * an unanswered area would otherwise re-nudge forever (or, with too long an
 * interval, go quiet for an hour). So the marker also carries a per-area nudge
 * counter: we re-nudge on a short interval (5 min) up to `ONBOARDING_MAX_NUDGES`
 * times, and the FINAL nudge flips to a stand-down variant that posts a polite
 * "let me know if you'd like me to stop; resume any time with /onboard-<code>"
 * to the manager, then sets `gaveUp` so no further nudges fire until `/onboard`
 * clears the marker. The area advancing (a real manager reply) resets the marker
 * on the new area, which restarts the count. This is the fast, manager-facing
 * exit; ENG-6602's 24h server-side gate stays as the deadlock backstop.
 *
 * Pure decision (`decideOnboardingDrive`) + directive text are unit-tested; the
 * marker file IO is thin and side-effecting.
 */

import { mkdirSync, readFileSync, rmSync, writeFileSync } from 'node:fs';
import { dirname } from 'node:path';
import {
  describeOnboardingChannel,
  isOnboardingArea,
  type OnboardingArea,
  type OnboardingChannel,
  type OnboardingStep,
} from '@augmented/core';

/**
 * Re-nudge an outstanding onboarding area after this long (self-heal + bounded
 * re-nudge). ENG-6626 shortened this from 20 min to 5 min so the bounded nudge
 * sequence (`ONBOARDING_MAX_NUDGES`) plays out in ~10-15 min rather than an hour.
 */
export const ONBOARDING_REINJECT_INTERVAL_MS = 5 * 60_000;

/**
 * How many times we nudge a single area before standing down (ENG-6626). The
 * first nudge is the kick-off; the last nudge is the stand-down message. With
 * the default 3 and a 5-min interval: kick-off, one reminder, then stand-down.
 */
export const ONBOARDING_MAX_NUDGES = 3;

/** Persisted per-agent marker: which drivable area we last injected, and when. */
export interface OnboardingDriveMarker {
  step: OnboardingArea;
  injectedAtMs: number;
  /**
   * How many times we've nudged THIS area (ENG-6626). 1 = the first kick-off.
   * Resets when the area changes (a new marker is written for the new area).
   */
  nudgeCount: number;
  /**
   * The stand-down message has been sent for this area (ENG-6626); no further
   * nudges fire until `/onboard` clears the marker. Stays false until the final
   * nudge.
   */
  gaveUp: boolean;
  /**
   * The `onboarding_state.generation` this marker was injected for (ENG-6601).
   * When the live state's generation changes (operator re-ran `/onboard`), the
   * driver re-injects even on the SAME step, so a same-area RESET is no longer
   * idempotency-swallowed. A legacy marker with no recorded generation reads
   * back as 0 (matching a pre-ENG-6601 state, which also has none ⇒ 0).
   */
  generation: number;
}

export interface OnboardingDriveDecision {
  /** Inject the directive into the session this tick. */
  inject: boolean;
  /** Remove the marker file (onboarding left the drivable phases). */
  clearMarker: boolean;
  /** Marker to persist after a confirmed-delivered inject (null = none). */
  nextMarker: OnboardingDriveMarker | null;
  /**
   * This inject is a staleness re-nudge of an area we've already nudged (same
   * step, outstanding past the re-inject interval), not the first nudge for it
   * (ENG-6583). The driver phrases it as a gentle "still waiting on you"
   * reminder rather than a fresh kick-off. Never set on the final stand-down
   * inject (`standDown` takes over there).
   */
  reminder: boolean;
  /**
   * This is the FINAL nudge for the area (ENG-6626): the manager hasn't engaged
   * after `ONBOARDING_MAX_NUDGES` nudges, so the directive flips to a stand-down
   * variant that posts a polite "resume with /onboard-<code>" to the manager and
   * stops. The persisted `nextMarker` carries `gaveUp: true`, so once this inject
   * reaches the session no further nudges fire until `/onboard` clears it.
   */
  standDown: boolean;
  /**
   * This inject was forced by a reset-generation change (ENG-6636): the operator
   * just re-ran `/onboard`, so onboarding restarted and the host re-injected even
   * though the step may be unchanged. The directive uses this to be unmistakable
   * that the agent must POST the area question afresh THIS turn rather than
   * treating the nudge as "I already asked, keep waiting" (the live scout drop).
   */
  freshRestart: boolean;
}

/**
 * Decide whether to inject an onboarding directive this poll. Pure — `nowMs` is
 * passed in for testability. `nextMarker` is only persisted by the caller AFTER
 * `injectMessage` confirms delivery, so an unhealthy/booting session naturally
 * retries on the next poll instead of being marked done prematurely.
 */
export function decideOnboardingDrive(
  step: OnboardingStep,
  marker: OnboardingDriveMarker | null,
  nowMs: number,
  generation: number = 0,
  reinjectIntervalMs: number = ONBOARDING_REINJECT_INTERVAL_MS,
  maxNudges: number = ONBOARDING_MAX_NUDGES,
): OnboardingDriveDecision {
  if (isOnboardingArea(step)) {
    const sameStep = marker?.step === step;
    const current = sameStep ? (marker as OnboardingDriveMarker) : null;

    // ENG-6601: a reset-generation bump (the operator re-ran `/onboard`, which
    // RESETs onboarding_state and bumps its generation) re-engages even the SAME
    // area the agent was already parked on. Without this, RESET landing back on
    // framing -> framing is idempotency-swallowed and `/onboard` looks dead.
    // Treat a generation change as a fresh kick-off: reset the nudge budget and
    // clear any prior stand-down. This fires only when the generation actually
    // changes (an explicit operator event), never per-poll, so there is no spam
    // loop (the rejected `completed == []` shortcut WOULD loop).
    const genChanged = current !== null && current.generation !== generation;
    if (genChanged) {
      return {
        inject: true,
        clearMarker: false,
        nextMarker: { step, injectedAtMs: nowMs, nudgeCount: 1, gaveUp: false, generation },
        reminder: false,
        standDown: false,
        // The operator just re-ran /onboard: tell the agent to ask afresh now.
        freshRestart: true,
      };
    }

    // We already stood down on this area (ENG-6626): stay silent until /onboard
    // clears the marker. A real manager reply advances the area, which lands a
    // fresh marker on the new step and restarts the count.
    if (current?.gaveUp) {
      return { inject: false, clearMarker: false, nextMarker: current, reminder: false, standDown: false, freshRestart: false };
    }

    const fresh = current === null;
    const stale = current !== null && nowMs - current.injectedAtMs >= reinjectIntervalMs;
    if (!fresh && !stale) {
      // Same area, same generation, not yet stale: wait for the agent / manager.
      return { inject: false, clearMarker: false, nextMarker: current, reminder: false, standDown: false, freshRestart: false };
    }

    // A new area is the kick-off (nudge 1); a stale same-area re-fire is the next
    // nudge in the bounded sequence. The final nudge stands down.
    const nudgeCount = fresh ? 1 : current.nudgeCount + 1;
    const isFinal = nudgeCount >= maxNudges;
    return {
      inject: true,
      clearMarker: false,
      nextMarker: { step, injectedAtMs: nowMs, nudgeCount, gaveUp: isFinal, generation },
      // A middle re-fire (not fresh, not final) is a gentle "still waiting"
      // reminder; the final one is a stand-down, not a reminder.
      reminder: !fresh && !isFinal,
      standDown: isFinal,
      freshRestart: false,
    };
  }
  // pending | ready — nothing to drive. Clear a stale marker so a later
  // /onboard (resets → the first area) is treated as a fresh area and re-drives.
  return { inject: false, clearMarker: marker !== null, nextMarker: null, reminder: false, standDown: false, freshRestart: false };
}

/** Per-area one-line summary of what the agent should do in this area. */
const AREA_DIRECTIVE: Record<OnboardingArea, string> = {
  framing:
    'Frame the work — ask your manager what matters in week one and what success looks like, then record it with memory_save',
  tasks:
    'Set up your recurring work — propose your role default scheduled tasks, let your manager pick which to set up, then call onboarding_provision_tasks with their choices and tune the result',
  integrations:
    'Wire up your tools — ask your manager which integrations your role uses and request access to the ones they confirm (connecting is a human OAuth step)',
  reporting:
    'Agree how you keep your manager posted — settle on a cadence and channel, then create a recurring report scheduled task for it',
};


/**
 * The directive injected into the session. Area-specific, concise, actionable.
 *
 * `completed` is the persisted onboarding trail (`onboarding_state.completed`).
 * When the agent has areas behind it — i.e. on RESUME or any mid-walk nudge — we
 * name them so it re-orients on the CURRENT area instead of re-interviewing
 * ground it already covered. `pending` and any non-area trail entries are
 * filtered out; only real walkable areas are surfaced.
 *
 * ENG-6583: onboarding is a CONVERSATION with the manager, not a self-drive. The
 * agent must ask THIS area's question in the channel onboarding was triggered
 * from (`opts.channel`) and WAIT for the reply before configuring or advancing;
 * never self-answer. `opts.reminder` re-phrases a staleness re-fire as a gentle
 * "still waiting on you" nudge so a manager who hasn't replied yet isn't kicked
 * as if onboarding just started.
 *
 * ENG-6626: `opts.standDown` flips to the final, polite exit. After the bounded
 * nudge budget is spent with no manager reply, the agent posts a one-off "let me
 * know if you'd like me to stop nudging; resume any time with /onboard-<code>"
 * to the manager and then stops. `opts.code` supplies the agent code for the
 * resume command (falls back to a bare `/onboard` when absent).
 *
 * ENG-6636: `opts.freshRestart` is set when the operator just re-ran `/onboard`
 * (the reset-generation re-inject). Live on scout, the agent read the generic
 * kickoff as "I already asked, keep waiting" and "stood down" without ever
 * posting the question. So the kickoff AND the restart variant now imperatively
 * tell the agent to POST the question as a NEW message THIS turn and forbid
 * "standing down" on the strength of a prior round's ask; the restart variant
 * additionally states outright that onboarding has been restarted.
 */
export function buildOnboardingDirective(
  step: OnboardingArea,
  completed: readonly OnboardingStep[] = [],
  opts: {
    channel?: OnboardingChannel;
    reminder?: boolean;
    standDown?: boolean;
    freshRestart?: boolean;
    code?: string;
  } = {},
): string {
  const { channel, reminder = false, standDown = false, freshRestart = false, code } = opts;
  const doneAreas = completed.filter(isOnboardingArea);
  const alreadyCovered =
    doneAreas.length > 0
      ? `You've already covered ${doneAreas.join(', ')} — re-orient, don't re-ask about those. `
      : '';
  // The conversation anchor: ask in the triggering channel and wait. Without a
  // channel on the state (legacy / channel-less) fall back to "your usual
  // channel": still ask-and-wait, just unanchored.
  // ENG-6855: don't assert the trigger channel is "your manager". It's wherever
  // onboarding was kicked off (often the owner or a teammate), not necessarily
  // the agent's reports_to manager. Conflating them led an agent to claim a
  // report reached its manager when it had gone to the owner's DM.
  const where = channel
    ? `whoever is guiding your onboarding in ${describeOnboardingChannel(channel)} (the channel onboarding was triggered from)`
    : 'your manager over your usual channel';
  // The wait half (shared): once the question is out, hold for the reply.
  const waitClause =
    `WAIT for their reply. Do NOT self-answer, and do NOT call \`onboarding_advance\` ` +
    `until they respond. ${AREA_DIRECTIVE[step]} only once they have. `;
  // The reminder keeps the gentler "ask if you haven't" phrasing.
  const askAndWait = `Ask ${where} this area's question and ${waitClause}`;
  // ENG-6636: the imperative post-now clause for the kickoff / restart paths.
  // The live failure was the agent treating a prior round's ask as done and
  // "standing down"; this makes the ask an action to take THIS turn.
  const mustAskNow =
    `Post this area's question to ${where} as a NEW message in THIS turn. Even if you think ` +
    `you asked in an earlier round, do NOT "stand down" to wait unless you have just asked it now; ` +
    `a previous round's ask does not count. Once it is out, ${waitClause}`;
  if (standDown) {
    // ENG-6626: nudge budget spent with no manager reply. Post the polite exit
    // ONCE and then go quiet; the manager resumes whenever they're ready. Do not
    // call onboarding_advance (the area is genuinely unanswered).
    const resumeCmd = code ? `/onboard-${code}` : '/onboard';
    return (
      `🛑 Onboarding (${step}): your manager hasn't replied after a few nudges, so stand down on this for now. ` +
      `Post exactly this once to ${where}, then STOP nudging this area and do NOT call \`onboarding_advance\`: ` +
      `"Let me know if you'd like me to stop nudging you on this. You can resume onboarding at any time using ${resumeCmd}." ` +
      `After posting, leave onboarding parked; your manager can resume it whenever they're ready.`
    );
  }
  if (reminder) {
    return (
      `⏳ Onboarding (${step}): still waiting on your manager's answer for this area. ` +
      `If you haven't asked yet, ask now; otherwise give them a gentle nudge, but don't move on without a reply. ` +
      askAndWait +
      `Advance with \`onboarding_advance\` (ADVANCE) once this area is done.`
    );
  }
  if (freshRestart) {
    // ENG-6636: the operator just re-ran /onboard. Be unmistakable that
    // onboarding restarted and the question must go out fresh THIS turn, so the
    // agent doesn't read the re-inject as "already asked, keep waiting".
    return (
      `🔄 Onboarding (${step}): your manager just re-ran \`/onboard\`, so onboarding has RESTARTED at this area. ` +
      `Call the \`onboarding_get\` tool now to re-read this area, then ${mustAskNow}` +
      `Advance with \`onboarding_advance\` (ADVANCE) once this area is done.`
    );
  }
  return (
    `🚀 Onboarding (${step}): your self-onboarding is active and waiting on you. ` +
    alreadyCovered +
    `Call the \`onboarding_get\` tool now to see this area's questions and full instructions, then ${mustAskNow}` +
    `Advance with \`onboarding_advance\` (ADVANCE) once this area is done.`
  );
}

/**
 * Did the onboarding nudge REACH the agent's session? (ENG-6576)
 *
 * The marker must be persisted once the nudge lands, NOT only on a *confirmed*
 * delivery. With acpx removed (ENG-6345) in-session delivery is tmux send-keys,
 * which is structurally `delivered:false` even when the keystroke lands — so
 * gating the marker write on `delivered` alone meant it was never written and
 * the driver re-injected the directive on every poll forever (the scout
 * re-nudge loop). `fallbackUsed` is the signal the keystroke reached the
 * session, so we treat `delivered || fallbackUsed` as "reached". When neither
 * is set the keystroke never landed: we deliberately do NOT persist, and the
 * 5-min staleness self-heal re-nudges next poll (ENG-6626), keeping the retry
 * safety net without the perpetual loop. Mirrors the scheduled-task route's `fallbackUsed`
 * read (see InjectResult in persistent-session.ts).
 */
export function onboardingNudgeReachedSession(result: {
  delivered: boolean;
  fallbackUsed: boolean;
}): boolean {
  return result.delivered || result.fallbackUsed;
}

/** Read + validate the marker; missing/corrupt/non-drivable → null. */
export function readOnboardingDriveMarker(path: string): OnboardingDriveMarker | null {
  try {
    const raw = JSON.parse(readFileSync(path, 'utf8')) as Partial<OnboardingDriveMarker>;
    if (
      typeof raw.step === 'string' &&
      isOnboardingArea(raw.step as OnboardingStep) &&
      typeof raw.injectedAtMs === 'number' &&
      Number.isFinite(raw.injectedAtMs)
    ) {
      // ENG-6626: nudgeCount/gaveUp were added after the marker shipped. A marker
      // written by an older manager won't carry them; treat it as "kicked off
      // once, not stood down" so the bounded sequence picks up from there rather
      // than restarting or wrongly believing it already stood down.
      const nudgeCount =
        typeof raw.nudgeCount === 'number' && Number.isFinite(raw.nudgeCount) && raw.nudgeCount >= 1
          ? Math.floor(raw.nudgeCount)
          : 1;
      // ENG-6601: a marker written before generation tracking shipped reads back
      // as generation 0, matching a pre-ENG-6601 state (also 0) so no spurious
      // re-inject fires the first time the upgraded manager sees an old marker.
      const generation =
        typeof raw.generation === 'number' && Number.isFinite(raw.generation) && raw.generation >= 0
          ? Math.floor(raw.generation)
          : 0;
      return {
        step: raw.step as OnboardingArea,
        injectedAtMs: raw.injectedAtMs,
        nudgeCount,
        generation,
        gaveUp: raw.gaveUp === true,
      };
    }
  } catch {
    /* missing or malformed — treat as no marker */
  }
  return null;
}

export function writeOnboardingDriveMarker(path: string, marker: OnboardingDriveMarker): void {
  mkdirSync(dirname(path), { recursive: true });
  writeFileSync(path, JSON.stringify(marker) + '\n', 'utf8');
}

export function clearOnboardingDriveMarker(path: string): void {
  try {
    rmSync(path, { force: true });
  } catch {
    /* best effort */
  }
}
